import React, { useState, useEffect } from "react";
import { IconButton, InputBase, Paper, Popper, List, ListItem, ListItemText, ClickAwayListener, Grow } from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";

const SearchWrapper = styled("div")({
    position: "relative",
    display: "flex",
    alignItems: "center",
});

const StyledPaper = styled(Paper)(({ expanded }) => ({
    display: "flex",
    alignItems: "center",
    width: expanded ? 280 : 40,
    transition: "all 300ms ease-in-out",
    borderRadius: expanded ? 20 : "8px",
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    border: "1px solid rgba(0, 0, 0, 0.06)",
}));

const StyledInputBase = styled(InputBase)(({ expanded }) => ({
    width: expanded ? "100%" : 0,
    opacity: expanded ? 1 : 0,
    transition: "all 300ms ease-in-out",
    "& .MuiInputBase-input": {
        padding: "8px 8px 8px 0",
        paddingLeft: "8px",
        fontSize: 14,
        color: "rgba(0, 0, 0, 0.7)",
        "&::placeholder": {
            color: "rgba(0, 0, 0, 0.4)",
        },
    },
}));

const StyledPopper = styled(Popper)({
    width: 280,
    marginTop: "4px",
    zIndex: 1000,
});

const ChatSearch = ({ messages, onMessageSelect }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [filteredMessages, setFilteredMessages] = useState([]);

    // Filter messages based on search text
    useEffect(() => {
        if (searchText) {
            const filtered = messages.filter((msg) => msg?.message?.toLowerCase().includes(searchText.toLowerCase()));
            setFilteredMessages(filtered);
        } else {
            setFilteredMessages([]);
        }
    }, [searchText, messages]);

    const handleSearchChange = (event) => {
        setSearchText(event.target.value);
        setAnchorEl(event.currentTarget);
    };

    const handleClickAway = () => {
        if (!searchText) {
            setIsExpanded(false);
        }
        setAnchorEl(null);
    };

    const handleMessageSelect = (messageId) => {
        onMessageSelect(messageId);
        setSearchText("");
        setAnchorEl(null);
        setIsExpanded(false);
    };

    const getMessagePreview = (text) => {
        const maxLength = 50;
        return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    const highlightSearchText = (text) => {
        if (!searchText) return text;

        const parts = text.split(new RegExp(`(${searchText})`, "gi"));
        return parts.map((part, index) =>
            part.toLowerCase() === searchText.toLowerCase() ? (
                <span key={index} style={{ backgroundColor: "rgba(255, 255, 0, 0.3)" }}>
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    const open = Boolean(anchorEl) && isExpanded && filteredMessages.length > 0;

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <SearchWrapper className=" ">
                <StyledPaper
                    sx={{
                        boxShadow: "none",
                        border: "none",
                        background: "var(--color-background_color)",
                    }}
                    expanded={isExpanded}
                    className=""
                >
                    <IconButton
                        onClick={() => setIsExpanded(true)}
                        sx={{
                            p: "10px",
                            background: "var(--color-background_color)",
                            boxShadow: "none",
                            border: "none",
                        }}
                        // className="tw-rounded-lg"
                    >
                        <SearchIcon sx={{ fontSize: 18, color: "action.active" }} className="tw-text-black tw-text-[20px]" />
                    </IconButton>
                    <StyledInputBase
                        expanded={isExpanded}
                        placeholder="Search in messages"
                        value={searchText}
                        onChange={handleSearchChange}
                        autoFocus={isExpanded}
                    />
                </StyledPaper>

                <StyledPopper open={open} anchorEl={anchorEl} placement="bottom-start" transition>
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                            <Paper
                                elevation={3}
                                sx={{
                                    borderRadius: "12px",
                                    marginTop: "8px",
                                    maxHeight: "300px",
                                    overflow: "auto",
                                }}
                            >
                                <List sx={{ py: 0 }}>
                                    {filteredMessages.map((message) => (
                                        <ListItem
                                            key={message.id}
                                            onClick={() => handleMessageSelect(message.id)}
                                            sx={{
                                                py: 1,
                                                px: 2,
                                                cursor: "pointer",
                                                "&:hover": {
                                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                                },
                                            }}
                                        >
                                            <ListItemText
                                                primary={highlightSearchText(getMessagePreview(message.message))}
                                                secondary={message.timestamp}
                                                primaryTypographyProps={{
                                                    sx: { fontSize: 14 },
                                                }}
                                                secondaryTypographyProps={{
                                                    sx: { fontSize: 12 },
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Grow>
                    )}
                </StyledPopper>
            </SearchWrapper>
        </ClickAwayListener>
    );
};

export default ChatSearch;
